// UPPDRAG-38 — admin UI: per-(membership tier) pricing for one league.
//
// Renders a row per active membership_plan in the league's organization.
// Each row has:
//   - "Gratis" switch (is_free)
//   - "% rabatt" number input (discount_percent), disabled when is_free
//   - read-only label showing the resulting price (computed from base + tier)
//
// On save: upserts ONE row per active plan into league_membership_pricing.
// Plans without an explicit row default to full price (no discount). When
// the admin clears the form (toggling all switches off + 0%), the upsert
// still writes 0%-no-free rows so the admin's intent is explicit.
//
// Fits inside LeagueForm.tsx's "Ekonomi" section. Mounts only when
// league?.id is set (we need an FK target).

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { NumberInput } from "@/components/ui/number-input";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Wallet } from "lucide-react";
import { toast } from "sonner";
import {
  calculateLeaguePriceForPlayer,
  type LeagueMembershipPricingRule,
} from "@/lib/leaguePricingCore";

interface MembershipPlanRow {
  id: string;
  name: string;
  sort_order: number | null;
}

interface PricingRowState {
  is_free: boolean;
  discount_percent: number;
}

interface Props {
  leagueId: string;
  organizationId: string;
  basePriceKr: number;
}

export default function LeagueMembershipPricing({
  leagueId,
  organizationId,
  basePriceKr,
}: Props) {
  const queryClient = useQueryClient();

  // 1. Active membership plans for this org (the rows).
  const { data: plans } = useQuery({
    queryKey: ["membership-plans-active", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("membership_plans" as any)
        .select("id, name, sort_order")
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      // Cast through unknown — supabase types don't infer the row shape for
      // `.from(... as any)` selects, and the runtime shape matches our type.
      return ((data ?? []) as unknown) as MembershipPlanRow[];
    },
    enabled: !!organizationId,
  });

  // 2. Existing pricing rules for this league.
  const { data: existingRules } = useQuery({
    queryKey: ["league-membership-pricing", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_membership_pricing" as any)
        .select("membership_plan_id, discount_percent, is_free")
        .eq("league_id", leagueId);
      if (error) throw error;
      return ((data ?? []) as unknown) as LeagueMembershipPricingRule[];
    },
    enabled: !!leagueId,
  });

  // 3. Local form state, keyed by plan_id.
  const [rowState, setRowState] = useState<Record<string, PricingRowState>>({});

  // Hydrate local state when plans + existing rules arrive.
  useEffect(() => {
    if (!plans) return;
    const next: Record<string, PricingRowState> = {};
    for (const plan of plans) {
      const existing = existingRules?.find((r) => r.membership_plan_id === plan.id);
      next[plan.id] = {
        is_free: existing?.is_free ?? false,
        discount_percent: existing?.discount_percent ?? 0,
      };
    }
    setRowState(next);
    // Re-hydrate when either input changes.
  }, [plans, existingRules]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!plans) return;
      const rows = plans.map((plan) => ({
        league_id: leagueId,
        membership_plan_id: plan.id,
        discount_percent: rowState[plan.id]?.discount_percent ?? 0,
        is_free: rowState[plan.id]?.is_free ?? false,
      }));
      const { error } = await supabase
        .from("league_membership_pricing" as any)
        .upsert(rows, { onConflict: "league_id,membership_plan_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Tier-priser sparade");
      queryClient.invalidateQueries({ queryKey: ["league-membership-pricing", leagueId] });
    },
    onError: (err: Error) => {
      console.error("[league-membership-pricing] save failed:", err);
      toast.error(`Kunde inte spara tier-priser: ${err.message}`);
    },
  });

  // For each row, compute the implied final price using the same calculator
  // the player-side will use. Keeps admin + player views in lock-step.
  const previewByPlanId = useMemo(() => {
    if (!plans) return {};
    const out: Record<string, ReturnType<typeof calculateLeaguePriceForPlayer>> = {};
    for (const plan of plans) {
      const state = rowState[plan.id];
      if (!state) continue;
      out[plan.id] = calculateLeaguePriceForPlayer({
        basePriceKr,
        playerActivePlanIds: [plan.id],
        rules: [
          {
            membership_plan_id: plan.id,
            discount_percent: state.discount_percent,
            is_free: state.is_free,
          },
        ],
        planNameById: { [plan.id]: plan.name },
      });
    }
    return out;
  }, [plans, rowState, basePriceKr]);

  if (!plans) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Inga aktiva medlemskapsnivåer i organisationen. Skapa medlemskapsnivåer i Inställningar → Medlemskap.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-muted-foreground" />
        <h4 className="font-medium text-sm">Medlemspriser per nivå</h4>
      </div>
      <p className="text-xs text-muted-foreground">
        Sätt rabatt eller fri seriepellsavgift per medlemsnivå. Spelare utan medlemskap betalar fullt pris ({basePriceKr} kr).
      </p>

      <div className="space-y-2">
        {plans.map((plan) => {
          const state = rowState[plan.id] ?? { is_free: false, discount_percent: 0 };
          const preview = previewByPlanId[plan.id];
          return (
            <div key={plan.id} className="flex flex-wrap items-center gap-3 rounded-md border p-3">
              <div className="flex-1 min-w-[120px]">
                <p className="text-sm font-medium">{plan.name}</p>
                {preview && (
                  <p className="text-xs text-muted-foreground">
                    {preview.isFree ? "Gratis" : `${preview.finalPriceKr} kr`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={state.is_free}
                  onCheckedChange={(v) =>
                    setRowState((prev) => ({
                      ...prev,
                      [plan.id]: { ...state, is_free: v },
                    }))
                  }
                />
                <span className="text-xs">Gratis</span>
              </div>
              <div className="flex items-center gap-2">
                <NumberInput
                  className="w-20"
                  value={state.discount_percent}
                  onValueChange={(v) =>
                    setRowState((prev) => ({
                      ...prev,
                      [plan.id]: {
                        ...state,
                        discount_percent: typeof v === "number" ? Math.max(0, Math.min(100, v)) : 0,
                      },
                    }))
                  }
                  min={0}
                  max={100}
                  disabled={state.is_free}
                />
                <span className="text-xs text-muted-foreground">% rabatt</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Sparar...</>
          ) : (
            <><Check className="h-3.5 w-3.5 mr-2" /> Spara medlemspriser</>
          )}
        </Button>
      </div>
    </div>
  );
}
